import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';

export enum NotificationTabValue {
  All = 'all',
  Unread = 'unread',
}

interface NotificationTrayTabsProps {
  activeTab: NotificationTabValue;
  onTabChange: (tab: NotificationTabValue) => void;
  /** Whether the unread tab should be disabled */
  isUnreadTabDisabled?: boolean;
}

/**
 * Tabs component for filtering notifications.
 * Allows switching between "All" and "Unread" views.
 * Unread tab is disabled when there are no unread notifications.
 */
export function NotificationTrayTabs({
  activeTab,
  onTabChange,
  isUnreadTabDisabled = false,
}: NotificationTrayTabsProps) {
  const { t } = useTranslation('authenticated.notifications');

  return (
    <div className="px-4 pb-4">
      <Tabs value={activeTab} onValueChange={value => onTabChange(value as NotificationTabValue)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value={NotificationTabValue.All}>{t('inbox.tabs.all')}</TabsTrigger>
          {isUnreadTabDisabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex w-full">
                  <TabsTrigger value={NotificationTabValue.Unread} disabled className="w-full">
                    {t('inbox.tabs.unread')}
                  </TabsTrigger>
                </span>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>
                  <p>{t('inbox.tabs.noUnreadTooltip')}</p>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          ) : (
            <TabsTrigger value={NotificationTabValue.Unread}>{t('inbox.tabs.unread')}</TabsTrigger>
          )}
        </TabsList>
      </Tabs>
    </div>
  );
}
